import { MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ExchangeRateDto, InvoiceDataPipelineConfigDto } from 'src/generated/services/TFinancialApi';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import Button from 'src/presentations/components/atom/button';
import {
  InvoiceDataPipelineConfigFilters,
  useDeleteInvoiceDataPipelineConfig,
  useGetInvoiceDataPipelineConfigMetaData,
  useGetInvoiceDataPipelineConfigs,
  useSaveInvoiceDataPipelineConfig,
} from 'src/use-cases/invoiceDataPipelineConfig/useInvoiceDataPipelineConfig';
import { newFormatDateTimeString } from 'src/utils/date';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { useGetServiceProvider } from 'src/use-cases/serviceProvider/useServiceProvider';
import { SettingsColumnHeader } from '../SettingsColumnHeader';
import { SettingsMain } from '../SettingsMain';

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
  width: '100px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

interface ModelConfigurationTableProps {
  searchQuery?: string;
}

function validateRequired(value: any): boolean {
  return value !== null && value !== '';
}

const validateData = (data: InvoiceDataPipelineConfigDto) => ({
  dataPipelineConfigId: !validateRequired(data.dataPipelineConfigId) ? 'Data Pipeline Config ID is Required' : '',
});

const columnSortKeyMap: Record<string, string> = {
  dataPipelineConfigIds: 'dataPipelineConfigId',
  modelId: 'modelId',
  active: 'active',
  updatedOn: 'updatedOn',
};

const columnMetadataMap: Record<string, string> = {
  dataPipelineConfigId: 'dataPipelineConfigIds',
  modelId: 'modelIds',
  active: 'active',
  updatedOn: 'updatedOn',
};

export const ModelConfigurationMain: FC<ModelConfigurationTableProps> = (props): JSX.Element => {
  const { searchQuery } = props;
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [filters, setFilters] = useState<InvoiceDataPipelineConfigFilters>({
    modelIds: [],
    dataPipelineConfigIds: [],
    active: [],
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [rowToDelete, setRowToDelete] = useState<InvoiceDataPipelineConfigDto | null>(null);
  const [isLoadingTableEntity, setIsLoadingTableEntity] = useState(false);
  const { data: serviceProviders } = useGetServiceProvider();

  // Use the enhanced table hook
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, clearValidationErrors } = useEnhancedTable<InvoiceDataPipelineConfigDto>({
    initialPageSize: 100,
    initialSorting: [
      {
        id: 'dataPipelineConfigId',
        desc: false,
      },
    ],
    onSearchQueryChange: searchQuery,
    requiredFields: ['dataPipelineConfigId', 'modelId'],
  });

  // Fetch config metadata data with filters
  const { data: metadataData, refetch: refetchMetadata } = useGetInvoiceDataPipelineConfigMetaData(filters);

  // Update the filter when pagination changes
  useEffect(() => {
    setIsInitialLoad(false);
  }, [isInitialLoad]);

  // Process sorting state for API request
  let sortingField = sorting.length > 0 ? sorting[0].id : undefined;
  let sortingDirection: 'ASC' | 'DESC' | undefined;

  if (sorting.length > 0) {
    sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';

    // Map column ID to backend field if needed
    if (columnSortKeyMap[sortingField]) {
      sortingField = columnSortKeyMap[sortingField];
    }
  }

  // Sorting is now directly used in the API request

  // Fetch data with pagination, sorting and filters directly
  const {
    data,
    isLoading: isDataLoading,
    isFetching: isDataFetching,
    refetch,
  } = useGetInvoiceDataPipelineConfigs(
    {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortingColum: sortingField,
      sortingDirection,
    },
    isInitialLoad ? undefined : filters
  );

  const invoiceDataPipelineConfigs = useMemo(() => {
    if (data?.data?.content && data?.data?.content.length > 0) {
      return data?.data?.content.map((item, index) => ({
        no: index + 1,
        dataPipelineConfigId: item?.dataPipelineConfigId,
        modelId: item?.modelId,
        active: item?.active,
        updatedOn: item?.updatedOn,
        id: item?.id,
      }));
    }
    return [];
  }, [data]);

  // Helper function to update a specific filter field
  const updateFilter = useCallback((key: keyof InvoiceDataPipelineConfigFilters, value: string[] | boolean[] | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Handler for table filter
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
      const isAllSelected = allOptions && selectedValues.length === allOptions.length;
      if (!selectedValues.length || isAllSelected) {
        switch (columnId) {
          case 'dataPipelineConfigId':
            updateFilter('dataPipelineConfigIds', undefined);
            break;
          case 'modelId':
            updateFilter('modelIds', undefined);
            break;
          case 'active':
            updateFilter('active', undefined);
            break;
          default:
            break;
        }
        refetchMetadata();
        refetch();
        return;
      }

      switch (columnId) {
        case 'dataPipelineConfigId':
          updateFilter('dataPipelineConfigIds', selectedValues);
          break;
        case 'modelId':
          updateFilter('modelIds', selectedValues);
          break;
        case 'active':
          // Convert string values to boolean for active filter
          const booleanValues = selectedValues.map((val) => val === 'true');
          updateFilter('active', booleanValues);
          break;
        default:
          break;
      }
    },
    [updateFilter, refetchMetadata, refetch]
  );

  // Handler for table header
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
    },
    [handleSortingChange]
  );

  const columns = useMemo(
    (): MRT_ColumnDef<InvoiceDataPipelineConfigDto>[] => [
      {
        enableEditing: false,
        accessorKey: 'no',
        header: 'NO',
        enableSorting: false,
      },
      {
        accessorKey: 'dataPipelineConfigId',
        header: 'DATA PIPELINE CONFIG ID',
        enableSorting: false,
        Header: ({ column }) => (
          <SettingsColumnHeader
            column={column}
            filterItems={createFilterItems(metadataData?.data?.dataPipelineConfigIds)}
            currentFilters={filters.dataPipelineConfigIds}
            onSort={handleTableSort}
            onFilter={handleTableFilter}
          />
        ),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.dataPipelineConfigId,
          helperText: validationErrors?.dataPipelineConfigId,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              dataPipelineConfigId: undefined,
            }),
        },
      },
      {
        accessorKey: 'modelId',
        enableSorting: false,
        Header: ({ column }) => (
          <SettingsColumnHeader
            column={column}
            filterItems={createFilterItems(metadataData?.data?.modelIds)}
            currentFilters={filters.modelIds}
            onSort={handleTableSort}
            onFilter={handleTableFilter}
          />
        ),
        header: 'MODEL ID',
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.modelId,
          helperText: validationErrors?.modelId,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              modelId: undefined,
            }),
        },
      },
      {
        accessorKey: 'active',
        enableSorting: false,
        Header: ({ column }) => (
          <SettingsColumnHeader
            column={column}
            filterItems={[
              { id: 'true', label: 'Active', count: 0 },
              { id: 'false', label: 'Inactive', count: 0 },
            ]}
            currentFilters={filters.active?.map((val) => val.toString())}
            onSort={handleTableSort}
            onFilter={handleTableFilter}
          />
        ),
        header: 'ACTIVE',
        editVariant: 'select',
        editSelectOptions: [
          { value: true, label: 'Active' },
          { value: false, label: 'Inactive' },
        ],
        muiEditTextFieldProps: {
          select: true,
          required: false,
          error: !!validationErrors?.active,
          helperText: validationErrors?.active,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              active: undefined,
            }),
        },
        Cell: ({ cell }) => (cell.row.original.active ? 'Active' : 'Inactive'),
      },
      {
        accessorKey: 'updatedOn',
        header: 'UPDATED ON',
        enableSorting: false,
        Header: ({ column }) => <SettingsColumnHeader column={column} filterItems={[]} onSort={handleTableSort} />,
        enableEditing: false,
        Cell: ({ cell }) => newFormatDateTimeString(cell.row.original.updatedOn),
      },
    ],
    [
      handleTableSort,
      handleTableFilter,
      metadataData?.data?.dataPipelineConfigIds,
      metadataData?.data?.modelIds,
      filters.dataPipelineConfigIds,
      filters.modelIds,
      filters.active,
      validationErrors,
    ]
  );

  const { mutateAsync: updateInvoiceDataPipelineConfig } = useSaveInvoiceDataPipelineConfig();
  const { mutateAsync: deleteInvoiceDataPipelineConfig } = useDeleteInvoiceDataPipelineConfig();

  // CREATE action
  const handleCreateInvoiceDataPipelineConfig: MRT_TableOptions<InvoiceDataPipelineConfigDto>['onCreatingRowSave'] = ({ values, table }) => {
    Object.keys(values).forEach((key) => {
      if (values[key] === '-' || values[key] === '') {
        values[key] = null;
      }
    });

    const newValidationErrors = validateData(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    updateInvoiceDataPipelineConfig(values)
      .then(() => {
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Invoice Data Pipeline Config created!',
        });
        setValidationErrors({});
        table.setCreatingRow(null);
      })
      .catch((error) => {
        openToastError(error instanceof Error ? error : new Error('Invoice Data Pipeline Config creation failed'));
      });
  };

  // UPDATE action
  const handleUpdateInvoiceDataPipelineConfig: MRT_TableOptions<InvoiceDataPipelineConfigDto>['onEditingRowSave'] = async ({ row, values, table }) => {
    // Change all "-" to null
    Object.keys(values).forEach((key) => {
      if (values[key] === '-' || values[key] === '') {
        values[key] = null;
      }
    }) as any;

    const newValidationErrors = validateData(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      await updateInvoiceDataPipelineConfig({
        ...values,
        id: row.original.id,
      });

      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Invoice Data Pipeline Config updated!',
      });
      table.setEditingRow(null);
    } catch (error) {
      openToastError(error instanceof Error ? error : new Error('Invoice Data Pipeline Config update failed'));
    }
  };

  // DELETE action
  const handlerDeleteConfirmModal = async (row: InvoiceDataPipelineConfigDto) => {
    setIsLoadingTableEntity(true);
    try {
      await deleteInvoiceDataPipelineConfig(row.id);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Invoice Data Pipeline Config deleted!',
      });
    } catch (error) {
      openToastError(error instanceof Error ? error : new Error('Invoice Data Pipeline Config delete failed'));
    } finally {
      setIsLoadingTableEntity(false);
    }
  };

  // Render row actions for exchange rates
  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const baseTableConfig = createTableConfig<ExchangeRateDto>({
    hiddenColumns: ['day', 'month', 'quarter'],
    onCreatingCancel: clearValidationErrors,
    onEditingCancel: clearValidationErrors,
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    onCreatingRowSave: handleCreateInvoiceDataPipelineConfig,
    onEditingRowSave: handleUpdateInvoiceDataPipelineConfig,
    data: invoiceDataPipelineConfigs,
    columns,
    state: {
      sorting,
      isLoading: isDataLoading || isDataFetching,
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        handleSortingChange(newSortingState);
      } else {
        handleSortingChange(updatedSorting);
      }
    },
    renderRowActions: ({ row, table }) => renderRowActions({ row, table }),
  });

  return (
    <SettingsMain
      title='Model Configurations'
      subtitle='Adjust the model configurations'
      backPath='/settings'
      topActions={
        <Button
          variant='primary'
          className='!pr-12 !pl-16 !ml-12'
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          <div className='flex items-center gap-10'>
            <span className='text-14 xl:text-16'>Add New</span>
            <AddIcon />
          </div>
        </Button>
      }
    >
      <SettingsBaseTable
        table={table}
        totalItems={data?.data?.totalElements || 0}
        pagination={pagination}
        onChangePage={onChangePage}
        onChangePageSize={onChangePageSize}
        pageSizeOptions={[100, 500, 1000]}
      />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isLoadingTableEntity}
        onAccept={async () => {
          await handlerDeleteConfirmModal(rowToDelete);
          setRowToDelete(null);
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this Invoice Data Pipeline Config?</p>
      </ModalConfirm>
    </SettingsMain>
  );
};
